'use client';

import React, { useMemo, useState } from 'react';
import {
  CarOutlined,
  ClockCircleOutlined,
  CompassOutlined,
  EnvironmentFilled,
  FlagOutlined,
  HomeOutlined,
  LockOutlined,
  StarOutlined,
} from '@ant-design/icons';
import { TripLeg, TripStop, TripStopKind } from '../types/trip';
import { summariseLegs } from '../utils/tripPlanner';

interface TripPlannerLadderProps {
  stops: TripStop[];
  legs: TripLeg[];
  onReorder: (stops: TripStop[]) => void;
  onRemoveStop?: (stopId: string) => void;
  title?: string;
  actions?: React.ReactNode;
  totalsOverride?: {
    distanceKm?: number;
    driveMinutes?: number;
  };
  compact?: boolean;
}

type DropPosition = 'before' | 'after';

/* ── Stop kind visual config ─────────────────────────────────────────────── */

const STOP_KIND_STYLES: Record<TripStopKind, { bg: string; label: string; icon: React.ReactNode }> = {
  start:    { bg: 'bg-sky-600',     label: 'Start',             icon: <EnvironmentFilled className="text-[9px]" /> },
  end:      { bg: 'bg-emerald-600', label: 'Destination',       icon: <FlagOutlined className="text-[9px]" /> },
  camp:     { bg: 'bg-amber-500',   label: 'Campsite',          icon: <HomeOutlined className="text-[9px]" /> },
  poi:      { bg: 'bg-violet-500',  label: 'Point of interest', icon: <StarOutlined className="text-[9px]" /> },
  waypoint: { bg: 'bg-blue-500',    label: 'Waypoint',          icon: <CompassOutlined className="text-[9px]" /> },
};

const StopTypeLabel: React.FC<{ kind: TripStopKind }> = ({ kind }) => {
  const style = STOP_KIND_STYLES[kind];
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-gray-400 mt-0.5">
      {style.icon}
      <span>{style.label}</span>
    </span>
  );
};

/* ── Helpers ──────────────────────────────────────────────────────────────── */

const formatDrive = (minutes?: number): string => {
  if (typeof minutes !== 'number' || Number.isNaN(minutes)) return '\u2014';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
};

const formatDistance = (distanceKm?: number): string => {
  if (typeof distanceKm !== 'number' || Number.isNaN(distanceKm)) return '\u2014';
  return `${distanceKm.toFixed(distanceKm >= 100 ? 0 : 1)} km`;
};

function reorderStops(
  stops: TripStop[],
  draggedId: string,
  targetId: string | null,
  position: DropPosition
): TripStop[] {
  const sourceIndex = stops.findIndex((stop) => stop.id === draggedId);
  if (sourceIndex === -1) return stops;

  const source = stops[sourceIndex];
  if (source.locked) return stops;

  const without = [...stops];
  without.splice(sourceIndex, 1);

  if (!targetId) {
    without.splice(without.length - 1, 0, source);
    return without;
  }

  const targetIndex = without.findIndex((stop) => stop.id === targetId);
  if (targetIndex === -1) return stops;

  const target = without[targetIndex];
  if (target.locked && target.kind === 'start' && position === 'before') {
    without.splice(targetIndex + 1, 0, source);
    return without;
  }

  if (target.locked && target.kind === 'end' && position === 'after') {
    without.splice(targetIndex, 0, source);
    return without;
  }

  const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
  without.splice(insertIndex, 0, source);
  return without;
}

/* ── Component ────────────────────────────────────────────────────────────── */

const TripPlannerLadder: React.FC<TripPlannerLadderProps> = ({
  stops,
  legs,
  onReorder,
  onRemoveStop,
  title = 'Trip Planner',
  actions,
  totalsOverride,
  compact = false,
}) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<DropPosition>('after');

  const legByOrigin = useMemo(() => {
    const map = new Map<string, TripLeg>();
    legs.forEach((leg) => map.set(leg.fromId, leg));
    return map;
  }, [legs]);

  const totals = useMemo(() => summariseLegs(legs), [legs]);
  const totalStops = stops.length;
  const distanceValue =
    totalsOverride?.distanceKm ?? totals.totalDistanceKm;
  const driveValue =
    totalsOverride?.driveMinutes ?? totals.totalDriveMinutes;
  const totalDistanceLabel = formatDistance(distanceValue);
  const totalDriveLabel = formatDrive(driveValue);
  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  const getBadgeLabel = (index: number): string => String.fromCharCode(65 + index);

  const getBadgeClasses = (stop: TripStop): string => {
    return `${STOP_KIND_STYLES[stop.kind].bg} text-white`;
  };

  const handleDragStart = (stop: TripStop) => {
    if (stop.locked) return;
    setDraggingId(stop.id);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setOverId(null);
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    stop: TripStop
  ) => {
    event.preventDefault();
    if (!draggingId || draggingId === stop.id) return;
    if (stop.locked && stop.id === stops[0].id) {
      setOverId(stop.id);
      setDropPosition('after');
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const before = event.clientY - rect.top < rect.height / 2;
    setOverId(stop.id);
    setDropPosition(before ? 'before' : 'after');
  };

  const handleDrop = (target: TripStop | null) => {
    if (!draggingId) return;
    const newStops = reorderStops(
      stops,
      draggingId,
      target ? target.id : null,
      dropPosition
    );
    onReorder(newStops);
    setDraggingId(null);
    setOverId(null);
  };

  /* ── Empty state ──────────────────────────────────────────────────────── */

  if (!stops.length) {
    return (
      <div className="w-full rounded-xl border border-dashed border-gray-200 bg-white px-4 py-5 text-center">
        <EnvironmentFilled className="text-2xl text-gray-300 mb-2" />
        <p className="text-sm font-medium text-gray-700">{title}</p>
        <p className="mt-1 text-xs text-gray-500">
          Add stops from search results to build your trip.
        </p>
        {actions && <div className="mt-3 flex justify-center gap-2">{actions}</div>}
      </div>
    );
  }

  /* ── Main render ──────────────────────────────────────────────────────── */

  return (
    <div className="flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Header with gradient accent */}
      <header className="relative border-b border-gray-100 bg-gray-50">
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-sky-500 via-blue-500 to-emerald-500" />
        <div className="flex items-center justify-between gap-2 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {firstStop?.name ?? 'Start'} &rarr; {lastStop?.name ?? 'Finish'}
            </h3>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
            <span className="font-medium">{totalStops} stops</span>
            <span className="text-gray-300">|</span>
            <span className="inline-flex items-center gap-1">
              <CarOutlined className="text-[11px]" />
              {totalDistanceLabel}
            </span>
            <span className="text-gray-300">|</span>
            <span className="inline-flex items-center gap-1">
              <ClockCircleOutlined className="text-[11px]" />
              {totalDriveLabel}
            </span>
          </div>
        </div>
      </header>

      {/* Stop list — unified timeline */}
      <div className="flex flex-col">
        {stops.map((stop, index) => {
          const legToNext = legByOrigin.get(stop.id);
          const isLast = index === stops.length - 1;
          const isDragging = draggingId === stop.id;
          const isTarget = overId === stop.id;

          return (
            <div key={stop.id} className="relative">
              {/* Drop indicator: before */}
              {isTarget && dropPosition === 'before' && (
                <div className="absolute -top-[1px] left-0 right-3 z-10 flex items-center">
                  <div className="ml-[11px] h-2.5 w-2.5 shrink-0 rounded-full border-2 border-blue-500 bg-white" />
                  <div className="h-[2px] flex-1 rounded-full bg-blue-500" />
                </div>
              )}

              <div
                draggable={!stop.locked}
                onDragStart={() => handleDragStart(stop)}
                onDragEnd={handleDragEnd}
                onDragOver={(event) => handleDragOver(event, stop)}
                onDrop={(event) => {
                  event.preventDefault();
                  handleDrop(stop);
                }}
                className={[
                  'group flex gap-3 px-3 transition-colors hover:bg-gray-50/80',
                  stop.locked
                    ? 'cursor-default'
                    : 'cursor-grab active:cursor-grabbing',
                  isDragging ? 'bg-blue-50/60' : '',
                ].join(' ')}
              >
                {/* Timeline column */}
                <div className="flex shrink-0 flex-col items-center w-8">
                  <div className={compact ? 'h-2 shrink-0' : 'h-3 shrink-0'} />
                  <div
                    className={[
                      'flex items-center justify-center rounded-full text-xs font-bold',
                      'h-8 w-8 ring-2 ring-white shadow-sm',
                      getBadgeClasses(stop),
                    ].join(' ')}
                  >
                    {getBadgeLabel(index)}
                  </div>
                  {!isLast && (
                    <div
                      className="w-0.5 flex-1 rounded-full bg-gray-200"
                      style={{ minHeight: '12px' }}
                    />
                  )}
                  {isLast && <div className={compact ? 'h-2 shrink-0' : 'h-3 shrink-0'} />}
                </div>

                {/* Content column */}
                <div className={`flex-1 min-w-0 ${compact ? 'py-1.5' : 'py-2.5'}`}>
                  {/* Name + controls */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 leading-tight">
                        {stop.name}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {stop.locked && (
                        <LockOutlined className="text-[10px] text-gray-300" />
                      )}
                      {onRemoveStop && !stop.locked && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onRemoveStop(stop.id);
                          }}
                          className="shrink-0 opacity-0 group-hover:opacity-100 text-[11px] font-medium text-gray-400 transition hover:text-red-500"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Stop type label */}
                  <StopTypeLabel kind={stop.kind} />

                  {/* Leg info chip */}
                  {!isLast && legToNext && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-gray-100/80 px-2.5 py-0.5 text-[11px] text-gray-400">
                      <CarOutlined className="text-[10px]" />
                      <span>{formatDistance(legToNext.distanceKm)}</span>
                      <span className="text-gray-300">&middot;</span>
                      <ClockCircleOutlined className="text-[10px]" />
                      <span>{formatDrive(legToNext.driveMinutes)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Drop indicator: after */}
              {isTarget && dropPosition === 'after' && (
                <div className="absolute -bottom-[1px] left-0 right-3 z-10 flex items-center">
                  <div className="ml-[11px] h-2.5 w-2.5 shrink-0 rounded-full border-2 border-blue-500 bg-white" />
                  <div className="h-[2px] flex-1 rounded-full bg-blue-500" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Actions footer */}
      {actions && (
        <div className="border-t border-gray-100 px-3 py-2.5">
          {actions}
        </div>
      )}
    </div>
  );
};

export default TripPlannerLadder;
